import { ceil, findIndex, round } from "lodash";
import {
  getCampaignStats,
  getCampaignEvents,
  getCampaignCustomReports,
  getCampaignStatsBySSOTFilter,
  getCampaignEventsBySSOTFilter,
} from "../APIs/CampaignAPI";
import {
  CAMPAIGN_STATS_API_FIELDS,
  REPORT_INTERVALS,
  STATS_LIST,
} from "../consts/Campaign";
import moment from "moment-timezone";
import { CAMPAIGN, NOTIFICATION, METADATA } from "../consts/DBFields.js";
import { TIME_INTERVALS, TIME_SCALE } from "../consts/DataViz";
import { getFolderEvents, getFolderEventsBySSOTFilter, getFolderStats } from "../APIs/FoldersAPI.js";
import { getTeamEvents, getTeamEventsBySSOTFilter } from "../APIs/TeamAPI.js";

export const serializeFolderAnalytics = (response) => {
  const viewStats = getViewClickConfig(
    response,
    "view_count",
    "unique_view_count"
  );

  const clickConfig = getViewClickConfig(
    response,
    "click_count",
    "unique_click_count"
  );

  // When click_metrics_available is false (e.g. non-personalized campaigns
  // that don't track opens/clicks), engagement metrics aren't meaningful
  // and should be shown as "N/A". Total Sends, Total Opens, and Invalid
  // Emails are still computed from the raw counts and shown as usual.
  const clickMetricsAvailable = response?.click_rate_metrics?.click_metrics_available !== false;

  const totalSend = response.sent_count_data?.buckets[0]?.sent_count?.value || 0;
  const totalOpens = viewStats.count || 0;
  const invalidEmails = response?.error_count_data?.buckets[0]?.error_count?.value || 0;

  const uniqueOpensValue = viewStats.uniqueCount || 0;
  const uniqueClicksValue = clickConfig.uniqueCount || 0;

  const clickRate = clickMetricsAvailable
    ? `${Number((clickConfig.percentage * 100).toFixed(2)) || 0} %`
    : "N/A";
  const uniqueOpens = clickMetricsAvailable ? uniqueOpensValue : "N/A";
  const uniqueClicks = clickMetricsAvailable ? uniqueClicksValue : "N/A";
  const openRate = clickMetricsAvailable
    ? `${Number((viewStats.percentage * 100).toFixed(2)) || 0} %`
    : "N/A";
  const clickPerOpen = clickMetricsAvailable
    ? `${Number((uniqueOpensValue ? (uniqueClicksValue / uniqueOpensValue) * 100 : 0).toFixed(2)) || 0} %`
    : "N/A";

  const rawAdvancedMetrics =
    response[CAMPAIGN_STATS_API_FIELDS?.VIEW_ADVANCED_METRICS];

  const advancedMetricsViewCount = getViewCount(rawAdvancedMetrics);
  const advancedMetrics = formatAdvancedMetrics(
    rawAdvancedMetrics,
    advancedMetricsViewCount
  );

  return {
    totalSend,
    totalOpens,
    clickRate,
    uniqueOpens,
    uniqueClicks,
    openRate,
    clickPerOpen,
    invalidEmails,
    advancedMetrics
  };
};

export const getViewCount = (metrics) => {
  const buckets =
    metrics?.[CAMPAIGN_STATS_API_FIELDS?.BROWSER_METRICS]?.["buckets"] || [];
  let viewCount = 0;
  buckets.forEach((bucket) => {
    viewCount += bucket?.doc_count;
  });
  return viewCount;
};

const getLabel = (key) => {
  let label;
  switch (key) {
    case "mail":
      label = "Email";
      break;
    case "zoom":
      label = "Zoom";
      break;
    case "teams":
      label = "Microsoft Teams";
      break;
    default:
      label = "";
  }
  return label;
};

const getViewClickConfig = (response, type, uniqueType) => {
  const count = response?.[type].doc_count;
  const percentage = count / response.notification_channel.doc_count;
  const uniqueCount = response?.[type]?.[uniqueType].value;

  const percentageOpensByStats = [];
  const countOpensByStats = [];
  const uniqueCountOpensByStats = [];

  response?.[type]?.notification_channel.metrics.buckets.forEach((bucket) => {
    percentageOpensByStats.push({
      label: getLabel(bucket.key),
      value: (bucket.doc_count / count) * 100,
    });
    countOpensByStats.push({
      label: getLabel(bucket.key),
      value: bucket.doc_count,
    });
  });

  return {
    count,
    percentage,
    uniqueCount,
    percentageOpensByStats,
    countOpensByStats,
    uniqueCountOpensByStats,
  };
};

export async function fetchCampaignStats(
  campaignId,
  trackAdvancedMetrics,
  isCampaignTable,
  campaignExecutionsDetails,
  startDate,
  endDate,
  filter,
  isPersonalized
) {
  let stats;
  //call the stats API
  if (filter?.filters && isPersonalized) {
    stats = await getCampaignStatsBySSOTFilter(
      campaignId,
      trackAdvancedMetrics,
      campaignExecutionsDetails,
      startDate,
      endDate,
      filter
    );
  }
  else {
    stats = await getCampaignStats(
      campaignId,
      trackAdvancedMetrics,
      campaignExecutionsDetails,
      startDate,
      endDate
    );
  }

  let sentCount = stats?.sent_count_data?.buckets[0]?.sent_count?.value || 0;
  let queuedCount = stats?.queued_count_data?.buckets[0]?.queued_count?.value || 0;
  let errorCount = stats?.error_count_data?.buckets[0]?.error_count?.value || 0;
  let totalDLCount = stats?.sent_count_data?.buckets[0]?.total_dl_count?.value || 0;
  let totalDLMembersCount = stats?.sent_count_data?.buckets[0]?.total_dl_members_count?.value || 0;
  let unsubscribedCount = stats?.unsubscribed_count_data?.buckets[0]?.unsubscribed_count?.value || 0;
  let notSentCount = stats?.not_sent_count_data?.buckets[0]?.not_sent_count?.value || 0;
  let campaignStatus = stats?.status[0]?.status

  let uniqueViews =
    stats &&
      stats.hasOwnProperty(CAMPAIGN_STATS_API_FIELDS.VIEWS) &&
      stats[CAMPAIGN_STATS_API_FIELDS.VIEWS].hasOwnProperty(CAMPAIGN_STATS_API_FIELDS.UNIQUE_VIEWS) &&
      stats[CAMPAIGN_STATS_API_FIELDS.VIEWS][CAMPAIGN_STATS_API_FIELDS.UNIQUE_VIEWS].hasOwnProperty("value")
      ? stats[CAMPAIGN_STATS_API_FIELDS.VIEWS][CAMPAIGN_STATS_API_FIELDS.UNIQUE_VIEWS].value
      : null;

  //open rate is unique view count divided by sent count times 100
  let openRate = sentCount ? (uniqueViews / sentCount) * 100 : 0;

  //handles javascript NaN response to 0/0 in "falsey" scenarios
  if (isNaN(openRate)) {
    openRate = 0;
  }

  //round to nearest 10nth
  openRate = Number((openRate).toFixed(2));

  if (!isCampaignTable) {
    let viewCount =
      stats &&
        stats.hasOwnProperty(CAMPAIGN_STATS_API_FIELDS.VIEWS) &&
        stats[CAMPAIGN_STATS_API_FIELDS.VIEWS].hasOwnProperty("doc_count")
        ? stats[CAMPAIGN_STATS_API_FIELDS.VIEWS].doc_count
        : null;

    let clickCount =
      stats &&
        stats.hasOwnProperty(CAMPAIGN_STATS_API_FIELDS.CLICKS) &&
        stats[CAMPAIGN_STATS_API_FIELDS.CLICKS].hasOwnProperty("doc_count")
        ? stats[CAMPAIGN_STATS_API_FIELDS.CLICKS].doc_count
        : null;

    let uniqueClicks =
      stats &&
        stats.hasOwnProperty(CAMPAIGN_STATS_API_FIELDS.CLICKS) &&
        stats[CAMPAIGN_STATS_API_FIELDS.CLICKS].hasOwnProperty(CAMPAIGN_STATS_API_FIELDS.UNIQUE_CLICKS) &&
        stats[CAMPAIGN_STATS_API_FIELDS.CLICKS][CAMPAIGN_STATS_API_FIELDS.UNIQUE_CLICKS].hasOwnProperty("value")
        ? stats[CAMPAIGN_STATS_API_FIELDS.CLICKS][CAMPAIGN_STATS_API_FIELDS.UNIQUE_CLICKS].value
        : null;

    let uniqueClickedUser =
      stats &&
        stats.hasOwnProperty(CAMPAIGN_STATS_API_FIELDS.UNIQUE_CLICKED_USER) &&
        stats[CAMPAIGN_STATS_API_FIELDS.UNIQUE_CLICKED_USER].hasOwnProperty(
          "count"
        ) &&
        stats[CAMPAIGN_STATS_API_FIELDS.UNIQUE_CLICKED_USER][
          "count"
        ].hasOwnProperty("value")
        ? stats[CAMPAIGN_STATS_API_FIELDS.UNIQUE_CLICKED_USER].count.value
        : stats &&
          stats[CAMPAIGN_STATS_API_FIELDS.UNIQUE_CLICKED_USER].hasOwnProperty(
            "COUNT"
          ) &&
          stats[CAMPAIGN_STATS_API_FIELDS.UNIQUE_CLICKED_USER][
            "COUNT"
          ].hasOwnProperty("value")
          ? stats[CAMPAIGN_STATS_API_FIELDS.UNIQUE_CLICKED_USER].COUNT.value
          : null;

    //click rate is unique clicked user count divided by sent count times 100
    let clickRate = sentCount ? (uniqueClickedUser / sentCount) * 100 : 0;

    //handles javascript NaN response to 0/0 in "falsey" scenarios
    if (isNaN(clickRate)) {
      clickRate = 0;
    }

    //round to nearest 10nth
    clickRate = Number((clickRate).toFixed(2));

    // click per open
    let clickPerOpen = uniqueViews ? Number((uniqueClicks / uniqueViews * 100).toFixed(2)) : 0;

    let advancedMetrics;
    if (viewCount) {
      const rawAdvancedMetrics =
        stats[CAMPAIGN_STATS_API_FIELDS.VIEW_ADVANCED_METRICS];
      advancedMetrics = formatAdvancedMetrics(rawAdvancedMetrics, viewCount);
    }
    return {
      openRate: openRate,
      recipients: sentCount,
      uniqueViews: uniqueViews,
      viewCount: viewCount,
      clickCount: clickCount,
      uniqueClicks: uniqueClicks,
      errorCount: errorCount,
      queuedCount: queuedCount,
      uniqueClickedUser: uniqueClickedUser,
      clickRate: clickRate,
      clickPerOpen: clickPerOpen,
      advancedMetrics,
      totalDLCount: totalDLCount,
      totalDLMembersCount: totalDLMembersCount,
      unsubscribedCount: unsubscribedCount,
      notSentCount: notSentCount,
      campaignStatus: campaignStatus
    };
  }

  return {
    openRate: openRate,
    recipients: sentCount,
    rawCampaignStats: stats,
    campaignStatus: campaignStatus
  };
}

export function formatAdvancedMetrics(advancedMetrics, viewCount) {
  const { BROWSER_METRICS, DEVICE_METRICS, OS_METRICS, DOC_COUNT } =
    CAMPAIGN_STATS_API_FIELDS;

  const browserMetrics = advancedMetrics?.[BROWSER_METRICS]?.buckets;
  const deviceMetrics = advancedMetrics?.[DEVICE_METRICS]?.buckets;
  const osMetrics = advancedMetrics?.[OS_METRICS]?.buckets;

  const mapUsageStats = (metrics = [], statsList) => {
    const mappedUsageStats = [];

    metrics.forEach((metric) => {
      const metricDocValue = round((metric?.[DOC_COUNT] / viewCount) * 100, 1);
      if (statsList.includes(metric?.key)) {
        mappedUsageStats.push({
          id: metric?.key,
          label: metric?.key,
          value: metricDocValue,
          count: metric?.[DOC_COUNT],
        });
      } else {
        const othersIndex = findIndex(mappedUsageStats, { id: "Others" });
        const updatedUsageStat = {
          id: "Others",
          label: "Others",
          value: metricDocValue + (mappedUsageStats[othersIndex]?.value || 0),
          count:
            metric?.[DOC_COUNT] + (mappedUsageStats[othersIndex]?.count || 0),
        };

        if (othersIndex === -1) mappedUsageStats.push(updatedUsageStat);
        else mappedUsageStats[othersIndex] = updatedUsageStat;
      }
    });
    return mappedUsageStats;
  };

  return {
    browserMetrics: mapUsageStats(browserMetrics, STATS_LIST.BROWSER),
    deviceMetrics: mapUsageStats(deviceMetrics, STATS_LIST.DEVICE),
    osMetrics: mapUsageStats(osMetrics, STATS_LIST.OS),
  };
}

export function formatChartDataAnalytics(res) {
  if (res) {
    //format date from ES
    let viewData = [];
    let clickData = [];
    // Date handling differs by aggregation interval (signaled by `interval`):
    //
    //   - Hourly ("1h"): backend returns exact UTC timestamps
    //     (e.g. "2026-05-27T20:00:00.000Z"). We pass the raw instant straight
    //     through so amCharts renders it in the browser's local timezone —
    //     e.g. 20:00 UTC shows as 15:00 on a CDT axis, matching the
    //     "All times are in CDT/CST" hint on the x-axis title.
    //
    //   - Daily/Weekly/Monthly ("1d" / "1w" / "1M"): backend returns
    //     UTC-midnight timestamps (e.g. "2026-05-20T00:00:00.000Z"). If we
    //     passed those raw, amCharts would format the instant in local time
    //     and label the bar with the previous calendar day in any negative-
    //     offset timezone (e.g. "May 19" in CDT). Instead, we take the
    //     YYYY-MM-DD portion of the string and construct a local-midnight
    //     Date for that same calendar date, so the axis label exactly
    //     matches the UTC date the API returned.
    const isHourly = res.view?.interval === "1h" || res.click?.interval === "1h";
    const toChartDate = (raw) => {
      if (isHourly) return new Date(Date.parse(raw));
      const [y, m, d] = raw.slice(0, 10).split("-").map(Number);
      return new Date(y, m - 1, d);
    };
    res.view.data.forEach(function (view) {
      viewData.push({ x: toChartDate(view.date), y: view.count });
    });

    res.click.data.forEach(function (click) {
      clickData.push({ x: toChartDate(click.date), y: click.count });
    });

    const clicksMap = new Map(clickData.map(item => [item.x.getTime(), item.y]));
    const historicalData = viewData.map(item => {
      return {
        x: item.x,
        views: item.y,
        clicks: clicksMap.get(item.x.getTime()) || 0
      };
    });

    return {
      viewData,
      clickData,
      historicalData
    };
  }
  return false;
}

export async function getFolderEventsForDatesAnalytics(folderId, startDate, end, selectedCampaignTypes, filters) {
  //logic to format ISO86 to the ElasticSearch date format
  let endDate;
  //if no endDate is passed, use first 24 hours
  if (!end) {
    endDate = moment(startDate).add(1, "days").toISOString();
  } else {
    endDate = end;
  }
  let events;
  if (filters) {
    // POST /folders/{id}/events/query — only when SSOT filters are active
    events = await getFolderEventsBySSOTFilter(
      folderId,
      startDate,
      endDate,
      selectedCampaignTypes,
      filters
    );
  } else {
    // GET /folders/{id}/events — default; supports CampaignType query param
    events = await getFolderEvents(
      folderId,
      startDate,
      endDate,
      selectedCampaignTypes
    );
  }
  let returnObj = {
    events: formatChartDataAnalytics(events),
    bucketSize: events.aggregateBy,
  };
  return returnObj;
};

export async function getTeamEventsForDatesAnalytics(teamId, startDate, end, selectedCampaignType, filters) {
  //logic to format ISO86 to the ElasticSearch date format
  let endDate;
  //if no endDate is passed, use first 24 hours
  if (!end) {
    endDate = moment(startDate).add(1, "days").toISOString();
  } else {
    endDate = end;
  }
  let events;
  if (filters) {
    // POST /teams/{id}/events/query — only when SSOT filters are active
    events = await getTeamEventsBySSOTFilter(
      teamId,
      startDate,
      endDate,
      selectedCampaignType,
      filters
    );
  } else {
    // GET /teams/{id}/events — default; supports CampaignType query param
    events = await getTeamEvents(
      teamId,
      startDate,
      endDate,
      selectedCampaignType
    );
  }
  let returnObj = {
    events: formatChartDataAnalytics(events),
    bucketSize: events.aggregateBy,
  };
  return returnObj;
};

export async function getCampaignEventsForDatesAnalytics(campaignId, startDate, end, filter, isPersonalized) {
  //logic to format ISO86 to the ElasticSearch date format
  let endDate;
  //if no endDate is passed, use first 24 hours
  if (!end) {
    endDate = moment(startDate).add(1, "days").toISOString();
  } else {
    endDate = end;
  }
  let events;
  if (filter?.filters && isPersonalized) {
    events = await getCampaignEventsBySSOTFilter(campaignId, startDate, endDate, filter);
  }
  else {
    events = await getCampaignEvents(campaignId, startDate, endDate);
  }
  let returnObj = {
    events: formatChartDataAnalytics(events),
    bucketSize: events.aggregateBy,
  };
  return returnObj;
}

function formatChartData(res) {
  if (res) {
    //format date from ES
    let viewData = [];
    let clickData = [];
    res.view.data.forEach(function (view) {
      viewData.push({ x: new Date(Date.parse(view.date)), y: view.count });
    });

    res.click.data.forEach(function (click) {
      clickData.push({ x: new Date(Date.parse(click.date)), y: click.count });
    });

    return [
      { id: "Total Opens", data: viewData },
      { id: "Total Clicks", data: clickData },
    ];
  }
  return false;
}

export async function getCampaignEventsForDates(campaignId, startDate, end) {
  //logic to format ISO86 to the ElasticSearch date format
  let endDate;
  //if no endDate is passed, use first 24 hours
  if (!end) {
    endDate = moment(startDate).add(1, "days").toISOString();
  } else {
    endDate = end;
  }
  let events = await getCampaignEvents(campaignId, startDate, endDate);
  let returnObj = {
    events: formatChartData(events),
    bucketSize: events.aggregateBy,
  };
  return returnObj;
}

//function to determine largest reasonable bucket size
export function determineBucketSize(startDT, endDT) {
  try {
    if (startDT && endDT) {
      //parse start/end time with moment
      var startTime = moment(startDT);
      var end = moment(endDT);

      //initialize the duration object
      var duration = moment.duration(end.diff(startTime));
      //verify the duration isn't negative
      if (Math.sign(duration.asHours()) === 1) {
        //use duration to determine "bucket size"
        if (duration.asDays() < 3) {
          // return hours bucket (< 3 days covers "Last 48 hours" without float drift)
          return REPORT_INTERVALS.HOURS;
        } else if (duration.asWeeks() <= 3) {
          // return days bucket
          return REPORT_INTERVALS.DAYS;
        } else if (duration.asWeeks() <= 26) {
          // return weeks bucket
          return REPORT_INTERVALS.WEEKS;
        } else if (duration.asMonths() <= 24) {
          // return months bucket
          return REPORT_INTERVALS.MONTHS;
        } else if (Math.round(duration.asYears()) <= 4) {
          //some rounding is needed to handle uneven but "close enough" year ranges
          // return quarters bucket
          return REPORT_INTERVALS.QUARTERS;
        } else if (
          duration.asYears() >= 1 ||
          Math.round(duration.asYears()) === 4
        ) {
          //some rounding is needed to handle uneven but "close enough" year ranges
          // return years bucket
          return REPORT_INTERVALS.YEARS;
        } else {
          throw new Error("There was an error parsing the date range");
        }
      } else {
        throw new Error(
          "determineBucketExam: The date range is negative or the dates are the same. Please Double check the values provided"
        );
      }
    } else {
      //handle dates not being passed
      throw new Error(
        "determineBucketExam: function was called without the proper parameters"
      );
    }
  } catch (err) {
    //handle general errors
    throw new Error(err);
  }
}

/**
 *
 * @param {string} bucketSize // hours, days, etc
 * @param {array} data // array of event data objects
 * @returns
 */
export function getChartTimeInterval(bucketSize, numberOfRecords = 0) {
  var scaleValue;
  var formatDateTime;
  const { TIME_FORMAT, DAY_FORMAT } = TIME_SCALE;
  switch (bucketSize) {
    case REPORT_INTERVALS.HOURS: //hour level formatting
      if (numberOfRecords >= 48) {
        //48+ hours formatting
        scaleValue = TIME_INTERVALS.SIX_HOURS;
        formatDateTime = TIME_FORMAT;
      } else {
        scaleValue = TIME_INTERVALS.THREE_HOURS; //24 hours formatting
        formatDateTime = TIME_FORMAT;
      }
      break;
    case REPORT_INTERVALS.DAYS: //day level formatting
      scaleValue = TIME_INTERVALS.ONE_DAY;
      formatDateTime = DAY_FORMAT;
      break;
    case REPORT_INTERVALS.WEEKS: //week level formatting
      scaleValue = TIME_INTERVALS.ONE_WEEK;
      formatDateTime = DAY_FORMAT;
      break;
    case REPORT_INTERVALS.MONTHS: //month level formatting
      scaleValue = TIME_INTERVALS.ONE_MONTH;
      formatDateTime = DAY_FORMAT;
      break;
    case REPORT_INTERVALS.QUARTERS: //quarters level formatting
      scaleValue = TIME_INTERVALS.ONE_QUARTER;
      formatDateTime = DAY_FORMAT;
      break;
    case REPORT_INTERVALS.YEARS: //years level formatting
      scaleValue = TIME_INTERVALS.ONE_YEAR;
      formatDateTime = DAY_FORMAT;
      break;
    default: //default to 24 hours as this is what the chart starts with
      scaleValue = TIME_INTERVALS.THREE_HOURS;
      formatDateTime = TIME_FORMAT;
  }
  return { scale: scaleValue, format: formatDateTime };
}

export async function getClickTrackingStatsForCampaign(campaign, startDate, endDate) {
  try {
    let hrefs_in_metadata = [];
    if (
      campaign[CAMPAIGN.UID] &&
      campaign?.[CAMPAIGN.NOTIFICATIONS][0]?.[NOTIFICATION.TYPE] &&
      campaign?.[CAMPAIGN.METADATA]?.[METADATA.HYPERLINK_INFO]?.[
      campaign[CAMPAIGN.NOTIFICATIONS][0][NOTIFICATION.TYPE]
      ]
    ) {
      //getting click tracking for 0 index alone
      hrefs_in_metadata =
        campaign?.[CAMPAIGN.METADATA]?.[METADATA.HYPERLINK_INFO]?.[
        campaign[CAMPAIGN.NOTIFICATIONS][0][NOTIFICATION.TYPE]
        ];
      if (hrefs_in_metadata) {
        let aggregate_by = "tracking_id",
          filters = `[event_name=click,campaign_id=${campaign[CAMPAIGN.UID]}]`;
        let clickTrackingStats = await getCampaignCustomReports(
          startDate,
          endDate,
          aggregate_by,
          filters
        );
        let total_clicks = 0;
        for (let href of hrefs_in_metadata) {
          href.count = 0;
          for (let trackingidObj of clickTrackingStats?.tracking_id) {
            if (trackingidObj[href?.trackingid]) {
              href.count = trackingidObj[href?.trackingid];
              total_clicks += href.count;
              break;
            }
          }
        }
        //calculate click rate
        for (let href of hrefs_in_metadata) {
          //round to 2 digits
          href.click_rate =
            Math.round(((href.count * 100) / total_clicks) * 100) / 100;
        }
      }
    }
    //sort by max click count
    hrefs_in_metadata.sort((obj1, obj2) => obj2.count - obj1.count);
    return hrefs_in_metadata;
  } catch (err) {
    //handle general errors
    throw new Error(err);
  }
}
